import moment from 'moment'
import numeral from 'numeral'
import React from 'react'
import { dateFormatTable } from 'utils'

import { Tooltip } from 'components/uiKit/Dropdown'
import Employee, { EmployeeById } from 'components/uiKit/Employee'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import Link from 'components/uiKit/Link'
import Loader from 'components/uiKit/Loader'
import { FileComponent, FolderComponent } from 'components/uiKit/Table'
import type { ColumnsType } from 'components/uiKit/Table/types'
import generateLink from 'routes/generateLink'
import { useLink, usePathParams } from 'routes/hooks'
import { selectEmployees } from 'services/Store/Users/selectors'
import { useAppSelector } from 'services/Store/hooks'
import { t } from 'services/Translation'
import type { FinderItem } from 'store/models'
import { extendTestData } from 'utils/test/qaData'
import { isFileProcessing, isFinderFile } from 'utils/useFinder'

import type { Paths } from '../types'
import * as s from './FinderTable.module.scss'

interface IProps {
  record: FinderItem
  clearQuery: () => void
  paths: Paths
}

const NameComponent: React.FC<IProps> = ({ record, paths, clearQuery }) => {
  const params = usePathParams(paths.main)
  const linkToFolder = generateLink(paths.main, { ...params, fileMetaGroupId: record.id })
  const linkToFile = useLink(paths.preview, { ...params, fileId: record.id })
  const showLoader = isFileProcessing(record)
  if (isFinderFile(record)) {
    return (
      <div className={s.name}>
        <div className={s.fileName}>
          <Tooltip mouseEnterDelay={1} overlay={record?.name} trigger={['hoverWithoutContent']}>
            <Link
              disabled={showLoader}
              name={`linkToFile${record.id}`}
              testData={extendTestData({
                id: record.id,
                createdBy: record.createdBy?.id,
                name: record.name,
              })}
              to={linkToFile}
            >
              <FileComponent disabled={showLoader} file={record} />
            </Link>
          </Tooltip>
        </div>
        {showLoader && (
          <Tooltip overlay={t('page.finder.videoProcessingInfo')}>
            <Loader name='loader' styleType='inline' />
          </Tooltip>
        )}
      </div>
    )
  }

  return (
    <Tooltip mouseEnterDelay={1} overlay={record?.name} trigger={['hoverWithoutContent']}>
      <Link
        name={record.id}
        onClick={clearQuery}
        testData={extendTestData({
          id: record.id,
          createdBy: record.createdBy?.id,
        })}
        to={linkToFolder}
        fluid
      >
        <FolderComponent folder={record} />
      </Link>
    </Tooltip>
  )
}

const FileUsageLink: React.FC<IProps & { fileUsages: number }> = ({
  record,
  paths,
  clearQuery,
  fileUsages,
}) => {
  const params = usePathParams(paths.main)
  const linkToFile = useLink(paths.preview, {
    ...params,
    fileId: record.id,
    filePreviewTabs: 'usage',
  })

  return fileUsages > 0 ? (
    <span className={s.usage}>
      <Icon name='otherApllay' size={KitSize.S} />
      <Link
        name='fileUsageLink'
        onClick={clearQuery}
        testData={extendTestData({
          id: record.id,
          createdBy: record.createdBy?.id,
        })}
        to={linkToFile}
      >
        {fileUsages}
      </Link>
    </span>
  ) : (
    ' '
  )
}

const NameComponentModal: React.FC<{
  record: FinderItem
  onClickItem: (item: FinderItem) => void
}> = ({ record, onClickItem }) => {
  const showLoader = isFileProcessing(record)
  return (
    <div className={s.name}>
      <div className={s.fileName}>
        <Tooltip mouseEnterDelay={1} overlay={record?.name} trigger={['hoverWithoutContent']}>
          <div onClick={() => onClickItem(record)}>
            {isFinderFile(record) ? (
              <FileComponent file={record} />
            ) : (
              <FolderComponent folder={record} />
            )}
          </div>
        </Tooltip>
      </div>
      {showLoader && (
        <Tooltip overlay={t('page.finder.videoProcessingInfo')}>
          <Loader name='loader' styleType='inline' />
        </Tooltip>
      )}
    </div>
  )
}

// TODO: change type on BE
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CreatedBy: React.FC<{ createdBy: any }> = ({ createdBy }) => {
  const employees = useAppSelector(selectEmployees)
  if (createdBy?.__typename === 'Employee') {
    return <EmployeeById id={createdBy.id} styleType='base' />
  } else if (createdBy?.__typename === 'KUser') {
    const employee = employees.find((e) => e.kUser.id === createdBy.id)
    if (!employee) {
      return null
    }

    return <Employee employee={employee} styleType='base' />
  }
  return null
}

export const COLUMN_BASE_CONFIG: ColumnsType<FinderItem> = [
  {
    title: t('table.title.format'),
    sortKey: 'ext',
    dataIndex: ['files', '0', 'ext'],
    width: 111,
    render: (ext, record) =>
      ext
        ? ext.toUpperCase()
        : record.__typename === 'FileMetaGroup'
          ? t('common.folder')
          : t('common.file'),
  },
  {
    title: t('table.title.size'),
    sortKey: 'size',
    dataIndex: 'size',
    align: 'right',
    width: 100,
    render: (size) => <span>{numeral(size).format('0.0 b')}</span>,
  },
  {
    title: t('common.usage'),
    sortKey: 'usageCount',
    dataIndex: 'usageCount',
    width: 120,
    render: (usageCount) =>
      usageCount > 0 ? (
        <span className={s.usage}>
          <Icon name='otherApllay' size={KitSize.S} />
          {usageCount}
        </span>
      ) : (
        ' '
      ),
  },
  {
    title: t('common.createdBy'),
    sortKey: 'createdBy',
    dataIndex: 'createdBy',
    width: 184,
    render: (createdBy) => <CreatedBy createdBy={createdBy} />,
  },
  {
    title: t('table.title.createDate'),
    dataIndex: 'createdAt',
    sortKey: 'createdAt',
    width: 120,
    render: (createdAt) => moment(createdAt).format(dateFormatTable),
  },
]

export const columnsConfig = (paths: Paths, clearQuery: () => void): ColumnsType<FinderItem> => [
  {
    title: t('table.title.title'),
    sortKey: 'name',
    dataIndex: 'name',
    width: 476,
    render: (_text, record) => (
      <NameComponent clearQuery={clearQuery} paths={paths} record={record} />
    ),
  },
  {
    title: t('table.title.format'),
    sortKey: 'ext',
    dataIndex: ['files', '0', 'ext'],
    width: 111,
    render: (ext) => (ext ? ext.toUpperCase() : t('common.folder')),
  },
  {
    title: t('table.title.size'),
    sortKey: 'size',
    dataIndex: 'size',
    align: 'right',
    width: 100,
    render: (size) => <span>{numeral(size).format('0.0 b')}</span>,
  },
  {
    title: t('common.usage'),
    sortKey: 'usageCount',
    dataIndex: 'usageCount',
    width: 120,
    render: (usageCount, record) => (
      <FileUsageLink
        clearQuery={clearQuery}
        fileUsages={usageCount}
        paths={paths}
        record={record}
      />
    ),
  },
  {
    title: t('common.createdBy'),
    sortKey: 'createdBy',
    dataIndex: 'createdBy',
    width: 184,
    render: (createdBy) => <CreatedBy createdBy={createdBy} />,
  },
  {
    title: t('table.title.createDate'),
    dataIndex: 'createdAt',
    sortKey: 'createdAt',
    width: 120,
    render: (createdAt) => moment(createdAt).format(dateFormatTable),
  },
]

export const columnsConfigModal = (
  onClickItem: (item: FinderItem) => void,
): ColumnsType<FinderItem> => [
  {
    title: t('table.title.title'),
    sortKey: 'name',
    dataIndex: 'name',
    width: 476,
    render: (_text, record) => <NameComponentModal onClickItem={onClickItem} record={record} />,
  },
  ...COLUMN_BASE_CONFIG,
]
